import { z } from 'zod';

const surveyEvaluationConfigItemSchema = z.object({
    formTemplateId: z.string().min(1),
    maxScore: z.number().finite().nonnegative().optional(),
    passingScore: z.number().finite().nonnegative().optional(),
});

const trainingBaseShape = {
    id: z.union([z.string(), z.number()]).optional(),
    title: z.string().optional().default('Untitled Training'),
    code: z.string().optional(),
    description: z.string().optional(),
    type: z.string().optional(),
    category: z.string().optional(),
    duration: z.string().optional(),
    scheduledDate: z.any().optional(),
    deadlineDate: z.any().optional(),
    instructor: z.any().optional(),
    externalInstructor: z.string().optional(),
    status: z.string().optional().default('draft'),
    trainedPersons: z.any().optional(),
    competencesGained: z.any().optional(),
    surveyFormTemplateIds: z.array(z.string()).optional().default([]),
    requireContentBeforeEvaluation: z.boolean().optional().default(true),
    evaluatorUserIds: z.array(z.string()).optional().default([]),
    scoringMode: z.enum(['per_survey', 'per_training', 'per_survey_aggregate']).optional().default('per_training'),
    evaluationMaxScore: z.number().finite().nonnegative().optional().nullable(),
    evaluationPassingScore: z.number().finite().nonnegative().optional().nullable(),
    surveyEvaluationConfig: z.array(surveyEvaluationConfigItemSchema).optional().default([]),
    additionalNotes: z.string().optional(),
    institutionResponsible: z.string().optional(),
    contentModules: z.array(z.object({
        id: z.string().min(1),
        type: z.enum(['video', 'text']),
        order: z.number().int().optional(),
        title: z.string().optional(),
        description: z.string().optional(),
        body: z.string().optional(),
        url: z.string().optional(),
        name: z.string().optional(),
        size: z.any().optional(),
        mimeType: z.string().optional(),
        storageKey: z.string().optional(),
    })).optional(),
    createdAt: z.any().optional(),
    updatedAt: z.any().optional(),
    customFields: z.record(z.unknown()).optional(),
};

export const DiscardTrainingVideoSchema = z.object({
    storageKey: z.string().min(1).optional(),
    url: z.string().min(1).optional(),
}).refine((value) => Boolean(value.storageKey || value.url), {
    message: 'storageKey or url is required',
});

function refineTrainingScoring(data, ctx, { requireEvaluators }) {
    const surveys = Array.isArray(data.surveyFormTemplateIds) ? data.surveyFormTemplateIds : [];
    if (surveys.length === 0) return;

    if (requireEvaluators) {
        const evaluators = Array.isArray(data.evaluatorUserIds) ? data.evaluatorUserIds : [];
        if (evaluators.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['evaluatorUserIds'],
                message: 'At least one evaluator is required when surveys are linked',
            });
        }
    }

    const mode = data.scoringMode || 'per_training';
    if (mode === 'per_survey') {
        const configs = Array.isArray(data.surveyEvaluationConfig) ? data.surveyEvaluationConfig : [];
        for (const sid of surveys) {
            const row = configs.find((c) => String(c.formTemplateId) === String(sid));
            if (!row) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['surveyEvaluationConfig'],
                    message: `Missing evaluation config for survey ${sid}`,
                });
                continue;
            }
            if (row.passingScore != null && row.maxScore != null && row.passingScore > row.maxScore) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['surveyEvaluationConfig'],
                    message: 'Passing score cannot exceed max score',
                });
            }
        }
    } else if (
        data.evaluationPassingScore != null &&
        data.evaluationMaxScore != null &&
        data.evaluationPassingScore > data.evaluationMaxScore
    ) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['evaluationPassingScore'],
            message: 'Passing score cannot exceed max score',
        });
    }
}

export const TrainingSchema = z.object(trainingBaseShape).superRefine((data, ctx) => {
    refineTrainingScoring(data, ctx, { requireEvaluators: true });
});

const { id: _idField, ...trainingCreateShape } = trainingBaseShape;

export const CreateTrainingSchema = z.object(trainingCreateShape).superRefine((data, ctx) => {
    refineTrainingScoring(data, ctx, { requireEvaluators: true });
});

export const UpdateTrainingSchema = z.object(
    Object.fromEntries(
        Object.entries(trainingBaseShape).map(([k, schema]) => [k, schema.optional()]),
    ),
).superRefine((data, ctx) => {
    if (data.surveyFormTemplateIds === undefined) return;
    const surveys = Array.isArray(data.surveyFormTemplateIds) ? data.surveyFormTemplateIds : [];
    if (surveys.length === 0) return;
    if (data.evaluatorUserIds !== undefined) {
        refineTrainingScoring(data, ctx, { requireEvaluators: true });
    } else {
        refineTrainingScoring(data, ctx, { requireEvaluators: false });
    }
});

export const TrainingFormSchema = TrainingSchema;
